import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';

/**
 * Panel for displaying user subscription and billing information
 */
const UserBillingPanel = ({ userId, onClose }) => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Fetch user billing information
   */
  const fetchBillingData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getToken();
      const response = await apiRequest(`/api/admin/users/${userId}/billing`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      setBillingData(data);

    } catch (error) {
      console.error('Error fetching billing data:', error);
      setError(error.message || 'Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Format currency amount
   */
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount / 100); // Assuming amounts are in cents
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  /**
   * Get subscription status badge styling
   */
  const getSubscriptionStatusBadge = (status) => {
    const baseClasses = 'px-3 py-1 rounded-full text-sm font-medium';
    
    switch (status?.toLowerCase()) {
      case 'active':
        return `${baseClasses} bg-green-900/30 text-green-400 border border-green-500/30`;
      case 'canceled':
        return `${baseClasses} bg-red-900/30 text-red-400 border border-red-500/30`;
      case 'past_due':
        return `${baseClasses} bg-yellow-900/30 text-yellow-400 border border-yellow-500/30`;
      case 'trialing':
        return `${baseClasses} bg-blue-900/30 text-blue-400 border border-blue-500/30`;
      default:
        return `${baseClasses} bg-gray-900/30 text-gray-400 border border-gray-500/30`;
    }
  };

  /**
   * Get payment status badge styling
   */
  const getPaymentStatusBadge = (status) => {
    const baseClasses = 'px-2 py-1 rounded text-xs font-medium';
    
    switch (status?.toLowerCase()) {
      case 'paid':
        return `${baseClasses} bg-green-900/30 text-green-400`;
      case 'pending':
        return `${baseClasses} bg-yellow-900/30 text-yellow-400`;
      case 'failed':
        return `${baseClasses} bg-red-900/30 text-red-400`;
      case 'refunded':
        return `${baseClasses} bg-purple-900/30 text-purple-400`;
      default:
        return `${baseClasses} bg-gray-900/30 text-gray-400`;
    }
  };

  // Load billing data when component mounts
  useEffect(() => {
    if (userId) {
      fetchBillingData();
    }
  }, [userId]);

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-700 rounded w-3/4"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <div className="text-center">
          <div className="text-red-400 mb-2">⚠️</div>
          <p className="text-red-300 mb-4">{error}</p>
          <button
            onClick={fetchBillingData}
            className="px-4 py-2 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors dark:text-white"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center">
        <h3 className="text-lg font-medium text-white dark:text-white">Billing & Subscription</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white dark:text-white"
        >
          ✕
        </button>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Current Subscription */}
        <div>
          <h4 className="text-md font-medium text-white mb-4 dark:text-white">Current Subscription</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                Plan
              </label>
              <p className="text-gray-300 capitalize dark:text-gray-600">
                {billingData?.subscription?.plan_name || 'Free'}
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                Status
              </label>
              <span className={getSubscriptionStatusBadge(billingData?.subscription?.status)}>
                {billingData?.subscription?.status || 'Free'}
              </span>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                Amount
              </label>
              <p className="text-gray-300 dark:text-gray-600">
                {billingData?.subscription?.amount 
                  ? `${formatCurrency(billingData.subscription.amount)} / ${billingData.subscription.interval}`
                  : 'Free'
                }
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                Next Billing Date
              </label>
              <p className="text-gray-300 dark:text-gray-600">
                {formatDate(billingData?.subscription?.current_period_end)}
              </p>
            </div>
          </div>
        </div>

        {/* Usage Statistics */}
        <div>
          <h4 className="text-md font-medium text-white mb-4 dark:text-white">Usage Statistics</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-700/50 rounded-lg p-4">
              <div className="text-2xl font-bold text-teal-400">
                {billingData?.usage?.total_calls || 0}
              </div>
              <div className="text-sm text-gray-400">Total Calls</div>
              <div className="text-xs text-gray-500 mt-1">
                {billingData?.usage?.calls_this_month || 0} this month
              </div>
            </div>
            
            <div className="bg-gray-700/50 rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-400">
                {billingData?.usage?.total_minutes || 0}
              </div>
              <div className="text-sm text-gray-400">Total Minutes</div>
              <div className="text-xs text-gray-500 mt-1">
                {billingData?.usage?.minutes_this_month || 0} this month
              </div>
            </div>
            
            <div className="bg-gray-700/50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-400">
                {formatCurrency(billingData?.usage?.total_cost || 0)}
              </div>
              <div className="text-sm text-gray-400">Total Cost</div>
              <div className="text-xs text-gray-500 mt-1">
                {formatCurrency(billingData?.usage?.cost_this_month || 0)} this month
              </div>
            </div>
          </div>
        </div>

        {/* Payment History */}
        <div>
          <h4 className="text-md font-medium text-white mb-4 dark:text-white">Recent Payments</h4>
          {billingData?.payments && billingData.payments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 text-sm font-medium text-gray-300 dark:text-gray-600">Date</th>
                    <th className="text-left py-2 text-sm font-medium text-gray-300 dark:text-gray-600">Amount</th>
                    <th className="text-left py-2 text-sm font-medium text-gray-300 dark:text-gray-600">Status</th>
                    <th className="text-left py-2 text-sm font-medium text-gray-300 dark:text-gray-600">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {billingData.payments.slice(0, 5).map((payment, index) => (
                    <tr key={index}>
                      <td className="py-2 text-sm text-gray-300 dark:text-gray-600">
                        {formatDate(payment.created)}
                      </td>
                      <td className="py-2 text-sm text-gray-300 dark:text-gray-600">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="py-2">
                        <span className={getPaymentStatusBadge(payment.status)}>
                          {payment.status}
                        </span>
                      </td>
                      <td className="py-2 text-sm text-gray-300 dark:text-gray-600">
                        {payment.description || 'Subscription payment'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">💳</div>
              <p className="text-gray-400">No payment history available</p>
            </div>
          )}
        </div>

        {/* Customer Information */}
        {billingData?.customer && (
          <div>
            <h4 className="text-md font-medium text-white mb-4 dark:text-white">Customer Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                  Customer ID
                </label>
                <p className="text-gray-300 font-mono text-sm dark:text-gray-600">
                  {billingData.customer.id}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                  Created
                </label>
                <p className="text-gray-300 dark:text-gray-600">
                  {formatDate(billingData.customer.created)}
                </p>
              </div>
              
              {billingData.customer.default_payment_method && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                    Default Payment Method
                  </label>
                  <p className="text-gray-300 dark:text-gray-600">
                    •••• •••• •••• {billingData.customer.default_payment_method.last4}
                    {' '}({billingData.customer.default_payment_method.brand})
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserBillingPanel;
